// ContactBook.js - Enhanced Contact Management Component
import React, { useState, useEffect, useRef } from 'react';
import { User, Phone, Building, Mail, FileText, Plus, Edit2, Trash2, X, Search, Save, Upload, Download, Clock, Tag, History } from 'lucide-react';
import apiService from './apiService';

const ContactBook = ({ darkMode, onCallContact }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [contactCallHistory, setContactCallHistory] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loadingMerge, setLoadingMerge] = useState(false);
  const [showLDAPImportModal, setShowLDAPImportModal] = useState(false);
  const [ldapImportConfig, setLdapImportConfig] = useState({
    fieldMappingId: null,
    departmentFilterId: null,
    customDepartments: [],
    dryRun: false
  });
  const [fieldMappings, setFieldMappings] = useState([]);
  const [departmentFilters, setDepartmentFilters] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    phone_number: '',
    full_name: '',
    company: '',
    email: '',
    notes: '',
    category: '',
    internal_number: '',
    second_number: ''
  });

  const theme = {
    bg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-400' : 'text-gray-600',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    input: darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  };

  useEffect(() => {
    loadContacts();
    loadLDAPConfiguration();
    
    // Listen for add contact from call event
    const handleAddFromCall = (event) => {
      const { phone_number, full_name, notes } = event.detail;
      setFormData({
        phone_number,
        full_name: full_name || '',
        company: '',
        email: '',
        notes: notes || '',
        category: '',
        internal_number: '',
        second_number: ''
      });
      setEditingContact(null);
      setShowModal(true);
    };
    
    window.addEventListener('addContactFromCall', handleAddFromCall);
    return () => window.removeEventListener('addContactFromCall', handleAddFromCall);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContacts({ search: searchTerm, limit: 500 });
      setContacts(response.data || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadLDAPConfiguration = async () => {
    try {
      const [mappingsResponse, filtersResponse] = await Promise.all([
        apiService.getLDAPFieldMappings(),
        apiService.getLDAPDepartmentFilters()
      ]);
      setFieldMappings(mappingsResponse || []);
      setDepartmentFilters(filtersResponse || []);
      
      // Set default selections
      const defaultMapping = mappingsResponse.find(m => m.is_default);
      const allDepartmentsFilter = filtersResponse.find(f => f.name === 'All Departments');
      
      setLdapImportConfig(prev => ({
        ...prev,
        fieldMappingId: defaultMapping?.id || null,
        departmentFilterId: allDepartmentsFilter?.id || null
      }));
    } catch (error) {
      console.error('Failed to load LDAP configuration:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      const response = await apiService.getLDAPImportHistory();
      setImportHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load import history:', error);
    }
  };

  const handleSearch = () => {
    loadContacts();
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setFormData({
      phone_number: '',
      full_name: '',
      company: '',
      email: '',
      notes: '',
      category: '',
      internal_number: '',
      second_number: ''
    });
    setShowModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setFormData({
      phone_number: contact.phone_number,
      full_name: contact.full_name || '',
      company: contact.company || '',
      email: contact.email || '',
      notes: contact.notes || '',
      category: contact.category || '',
      internal_number: contact.internal_number || '',
      second_number: contact.second_number || ''
    });
    setShowModal(true);
  };

  const handleSaveContact = async () => {
    try {
      // Validate required fields
      if (!formData.phone_number || !formData.full_name) {
        alert('Phone number and name are required');
        return;
      }

      // Clean and validate phone number
      const cleanPhone = formData.phone_number.replace(/[\s\-()]/g, '');
      if (cleanPhone.length < 4) {
        alert('Please enter a valid phone number (at least 4 digits)');
        return;
      }

      // Validate email if provided
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        alert('Please enter a valid email address');
        return;
      }

      // Use cleaned phone number
      const dataToSave = {
        ...formData,
        phone_number: cleanPhone
      };

      if (editingContact) {
        await apiService.updateContact(editingContact.phone_number, dataToSave);
      } else {
        await apiService.createContact(dataToSave);
      }

      setShowModal(false);
      loadContacts();
      alert(editingContact ? 'Contact updated!' : 'Contact added!');
    } catch (error) {
      console.error('Failed to save contact:', error);
      alert('Failed to save contact: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteContact = async (phoneNumber) => {
    if (!window.confirm('Delete this contact?')) return;

    try {
      await apiService.deleteContact(phoneNumber);
      loadContacts();
      alert('Contact deleted');
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Failed to delete contact');
    }
  };

  const handleCallContact = (phoneNumber) => {
    if (onCallContact) {
      onCallContact(phoneNumber);
    }
  };

  // Parse CSV line properly handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Import from CSV/Excel
  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('File is too large. Maximum size is 5MB.');
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      alert('Excel files (.xlsx, .xls) are not yet supported. Please export to CSV format first.');
      event.target.value = ''; // Reset file input
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert('File is empty or has no data rows.');
          return;
        }

        const imported = [];
        const errors = [];

        // Skip header row
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          try {
            const parts = parseCSVLine(line);
            
            // Validate required fields
            if (!parts[0] || !parts[1]) {
              errors.push(`Line ${i + 1}: Missing phone number or name`);
              continue;
            }

            // Clean and validate phone number
            const phoneNumber = parts[0].replace(/[\s\-()]/g, '');
            
            // Enhanced phone number validation
            if (phoneNumber.length < 4) {
              errors.push(`Line ${i + 1}: Invalid phone number format (minimum 4 digits)`);
              continue;
            }
            
            // Validate phone number contains only digits and allowed characters
            if (!/^[\d+*#]+$/.test(phoneNumber)) {
              errors.push(`Line ${i + 1}: Phone number contains invalid characters (only digits, +, *, # allowed)`);
              continue;
            }
            
            // Validate reasonable phone number length (4-20 digits)
            if (phoneNumber.length > 20) {
              errors.push(`Line ${i + 1}: Phone number too long (maximum 20 digits)`);
              continue;
            }

            imported.push({
              phone_number: phoneNumber,
              full_name: parts[1],
              company: parts[2] || '',
              email: parts[3] || '',
              notes: parts[4] || '',
              category: parts[5] || ''
            });
          } catch (err) {
            errors.push(`Line ${i + 1}: Parse error`);
          }
        }

        if (imported.length === 0) {
          alert('No valid contacts found in file.\n\n' + errors.join('\n'));
          return;
        }

        // Show preview if there are errors
        if (errors.length > 0 && errors.length < 10) {
          const proceed = window.confirm(
            `Found ${imported.length} valid contacts and ${errors.length} errors.\n\n` +
            errors.slice(0, 5).join('\n') +
            (errors.length > 5 ? `\n... and ${errors.length - 5} more errors` : '') +
            '\n\nProceed with import?'
          );
          if (!proceed) return;
        }

        // Import contacts
        importContacts(imported);
      } catch (error) {
        console.error('Import error:', error);
        alert('Failed to import contacts: ' + error.message);
      }
    };
    
    reader.onerror = () => {
      alert('Failed to read file');
    };
    
    reader.readAsText(file);
    event.target.value = ''; // Reset file input
  };

  const importContacts = async (contactsToImport) => {
    let success = 0;
    let failed = 0;
    let updated = 0;
    let skipped = 0;
    let updateMode = null; // null = ask, true = update all, false = skip all
    const failedContacts = [];

    for (const contact of contactsToImport) {
      try {
        // Check if contact already exists
        const existing = await apiService.lookupContact(contact.phone_number);
        
        if (existing && existing.data) {
          // Contact exists, handle duplicate
          if (updateMode === null) {
            // First duplicate, ask user
            const shouldUpdate = window.confirm(
              `Contact ${contact.full_name} (${contact.phone_number}) already exists.\n\n` +
              `Click OK to update all existing contacts, Cancel to skip all duplicates.`
            );
            updateMode = shouldUpdate;
          }
          
          if (updateMode) {
            await apiService.updateContact(contact.phone_number, contact);
            updated++;
          } else {
            skipped++;
          }
          continue;
        }

        // Contact doesn't exist, create it
        await apiService.createContact(contact);
        success++;
      } catch (error) {
        failed++;
        failedContacts.push(`${contact.full_name} (${contact.phone_number}): ${error.message || 'Unknown error'}`);
      }
    }

    let message = `Import complete!\n\nCreated: ${success}`;
    if (updated > 0) message += `\nUpdated: ${updated}`;
    if (skipped > 0) message += `\nSkipped: ${skipped}`;
    if (failed > 0) {
      message += `\nFailed: ${failed}`;
      if (failedContacts.length > 0 && failedContacts.length <= 5) {
        message += '\n\nErrors:\n' + failedContacts.join('\n');
      }
    }
    
    alert(message);
    loadContacts();
  };

  // Enhanced LDAP import with configuration
  const handleImportLDAP = () => {
    setShowLDAPImportModal(true);
  };

  const handleLDAPImportExecute = async () => {
    try {
      setLoading(true);
      console.log('🔍 Starting enhanced LDAP import...');
      
      const response = await apiService.importLDAPContacts(ldapImportConfig);
      
      if (response.success) {
        const { stats, importId, canRollback } = response;
        let message = `Enhanced LDAP Import Complete!\n\n`;
        message += `Total LDAP users: ${stats.total}\n`;
        message += `Imported: ${stats.imported}\n`;
        message += `Updated: ${stats.updated}\n`;
        message += `Skipped: ${stats.skipped}`;
        
        if (stats.errors > 0) {
          message += `\nErrors: ${stats.errors}`;
          if (response.errors && response.errors.length > 0) {
            message += '\n\nFirst few errors:\n' + response.errors.join('\n');
          }
        }
        
        if (canRollback) {
          message += `\n\nImport ID: ${importId} (can be rolled back from history)`;
        }
        
        alert(message);
        loadContacts(); // Refresh the contacts list
        setShowLDAPImportModal(false);
      } else {
        throw new Error(response.details || 'LDAP import failed');
      }
    } catch (error) {
      console.error('LDAP import error:', error);
      alert(`LDAP import failed: ${error.message || 'Unknown error'}\n\nPlease check that LDAP is configured correctly.`);
    } finally {
      setLoading(false);
    }
  };

  const handleLDAPPreview = async () => {
    try {
      setLoading(true);
      const previewConfig = { ...ldapImportConfig, dryRun: true };
      const response = await apiService.importLDAPContacts(previewConfig);
      
      if (response.success && response.preview) {
        const { preview } = response;
        let message = `LDAP Import Preview\n\n`;
        message += `Total users found: ${preview.total}\n`;
        message += `Department filter: ${preview.departmentFilter || 'All Departments'}\n\n`;
        message += `Sample users (first 10):\n`;
        preview.users.slice(0, 10).forEach(user => {
          message += `• ${user.displayName || user.cn} (${user.mobile}) - ${user.department || 'No dept'}\n`;
        });
        
        alert(message);
      }
    } catch (error) {
      console.error('LDAP preview error:', error);
      alert(`Preview failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRollbackImport = async (importId) => {
    if (!window.confirm('Rollback this import?\n\nThis will undo all changes made during this import. This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.rollbackLDAPImport(importId);
      
      if (response.success) {
        alert(`Rollback completed!\n\nRolled back: ${response.rolledBack} contacts`);
        loadContacts();
        loadImportHistory();
      }
    } catch (error) {
      console.error('Rollback error:', error);
      alert(`Rollback failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const csv = [
      ['Phone Number', 'Full Name', 'Company', 'Email', 'Notes', 'Category'].join(','),
      ...contacts.map(c => [
        c.phone_number,
        `"${c.full_name || ''}"`,
        `"${c.company || ''}"`,
        `"${c.email || ''}"`,
        `"${(c.notes || '').replace(/"/g, '""')}"`,
        `"${c.category || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Find duplicate contacts
  const findDuplicates = () => {
    const groups = {};
    
    contacts.forEach(contact => {
      if (!contact.full_name) return;
      
      // Clean name for grouping (remove parentheses and extra spaces)
      const cleanName = contact.full_name
        .replace(/\s*\([^)]*\)\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (!groups[cleanName]) {
        groups[cleanName] = [];
      }
      groups[cleanName].push(contact);
    });
    
    // Filter groups with more than 1 contact
    const duplicates = Object.entries(groups)
      .filter(([name, contacts]) => contacts.length > 1)
      .map(([name, contacts]) => ({
        cleanName: name,
        contacts: contacts.sort((a, b) => b.phone_number.length - a.phone_number.length)
      }));
    
    return duplicates;
  };

  const handleShowMerge = () => {
    const duplicates = findDuplicates();
    setDuplicateGroups(duplicates);
    setShowMergeModal(true);
  };

  const handleMergeGroup = async (group) => {
    try {
      setLoadingMerge(true);
      
      // Keep the contact with the longest phone number (likely external)
      const mainContact = group.contacts[0];
      const duplicateContacts = group.contacts.slice(1);
      
      // Collect internal numbers from duplicates
      const internalNumbers = duplicateContacts
        .map(c => c.phone_number)
        .filter(phone => phone.length <= 4 && /^\d+$/.test(phone));
      
      // Update main contact with internal number
      const updateData = {
        full_name: mainContact.full_name, // Keep original full name instead of cleanName
        internal_number: internalNumbers[0] || mainContact.internal_number,
        notes: [mainContact.notes, ...duplicateContacts.map(c => c.notes)]
          .filter(Boolean)
          .join('; ') || mainContact.notes
      };
      
      await apiService.updateContact(mainContact.phone_number, updateData);
      
      // Delete duplicate contacts
      for (const duplicate of duplicateContacts) {
        await apiService.deleteContact(duplicate.phone_number);
      }
      
      alert(`Merged ${group.contacts.length} contacts for "${group.cleanName}"`);
      loadContacts(); // Refresh the list
      
    } catch (error) {
      console.error('Merge failed:', error);
      alert('Failed to merge contacts');
    } finally {
      setLoadingMerge(false);
    }
  };

  // View call history for contact
  const handleViewCallHistory = async (contact) => {
    try {
      setSelectedContact(contact);
      setShowCallHistory(true);
      
      // Fetch call history for this number
      const response = await apiService.getCDRRecords({
        phone: contact.phone_number,
        limit: 50
      });
      
      setContactCallHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load call history:', error);
      alert('Failed to load call history');
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(contacts.map(c => c.category).filter(Boolean))];

  // Filter contacts by category
  const filteredContacts = categoryFilter === 'all' 
    ? contacts 
    : contacts.filter(c => c.category === categoryFilter);

  return (
    <div className={`${theme.bg} rounded-lg shadow-lg p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-2xl font-bold ${theme.text}`}>
          <User className="w-6 h-6 inline mr-2" />
          Contact Book ({filteredContacts.length})
        </h2>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFile}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            title="Import from CSV/Excel"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={handleImportLDAP}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            title="Import users from LDAP with advanced options"
          >
            <User className="w-4 h-4" />
            Import LDAP
          </button>
          <button
            onClick={() => {
              loadImportHistory();
              setShowImportHistory(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            title="View LDAP import history and rollback"
          >
            <Clock className="w-4 h-4" />
            History
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleShowMerge}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
            title="Merge Duplicate Contacts"
          >
            <User className="w-4 h-4" />
            Merge Duplicates
          </button>
          <button
            onClick={handleAddContact}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.textSecondary}`} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search contacts..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Search
          </button>
        </div>
        
        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Tag className={`w-4 h-4 ${theme.textSecondary}`} />
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  categoryFilter === cat
                    ? 'bg-blue-600 text-white'
                    : `${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'} hover:bg-blue-500 hover:text-white`
                }`}
              >
                {cat === 'all' ? 'All' : cat} ({cat === 'all' ? contacts.length : contacts.filter(c => c.category === cat).length})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contacts List */}
      {loading ? (
        <div className={`text-center py-12 ${theme.textSecondary}`}>
          Loading contacts...
        </div>
      ) : contacts.length === 0 ? (
        <div className={`text-center py-12 ${theme.textSecondary}`}>
          <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No contacts found</p>
          <button
            onClick={handleAddContact}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Add your first contact
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContacts.map((contact) => (
            <div
              key={contact.phone_number}
              className={`${theme.bg} border ${theme.border} rounded-lg p-4 ${theme.hover} transition-colors`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${theme.text} text-lg`}>
                      {contact.full_name}
                    </h3>
                    {contact.category && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {contact.category}
                      </span>
                    )}
                  </div>
                  {contact.company && (
                    <p className={`text-sm ${theme.textSecondary} flex items-center gap-1 mt-1`}>
                      <Building className="w-3 h-3" />
                      {contact.company}
                    </p>
                  )}
                  {contact.internal_number && (
                    <p className={`text-sm ${theme.textSecondary} flex items-center gap-1 mt-1`}>
                      <Phone className="w-3 h-3" />
                      Ext: {contact.internal_number}
                    </p>
                  )}
                  {contact.second_number && (
                    <p className={`text-sm ${theme.textSecondary} flex items-center gap-1 mt-1`}>
                      <Phone className="w-3 h-3" />
                      Second: {contact.second_number}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleViewCallHistory(contact)}
                    className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                    title="Call History"
                  >
                    <History className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEditContact(contact)}
                    className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteContact(contact.phone_number)}
                    className="p-1 text-red-600 hover:bg-red-100 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className={`w-4 h-4 ${theme.textSecondary}`} />
                  <span className={`text-sm ${theme.text} font-mono`}>
                    {contact.phone_number}
                  </span>
                  <button
                    onClick={() => handleCallContact(contact.phone_number)}
                    className="ml-auto px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                  >
                    Call
                  </button>
                </div>

                {contact.email && (
                  <div className="flex items-center gap-2">
                    <Mail className={`w-4 h-4 ${theme.textSecondary}`} />
                    <span className={`text-sm ${theme.textSecondary} truncate`}>
                      {contact.email}
                    </span>
                  </div>
                )}

                {contact.notes && (
                  <div className="flex items-start gap-2 mt-2">
                    <FileText className={`w-4 h-4 ${theme.textSecondary} mt-0.5`} />
                    <span className={`text-xs ${theme.textSecondary} line-clamp-2`}>
                      {contact.notes}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.bg} rounded-lg shadow-xl max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-bold ${theme.text}`}>
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className={`${theme.textSecondary} hover:${theme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Phone Number *
                </label>
                <input
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  disabled={!!editingContact}
                  placeholder="380XXXXXXXXX"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Second Number
                </label>
                <input
                  type="text"
                  value={formData.second_number}
                  onChange={(e) => setFormData({ ...formData, second_number: e.target.value })}
                  placeholder="380XXXXXXXXX"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Comfy LLC"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Internal Number
                </label>
                <input
                  type="text"
                  value={formData.internal_number}
                  onChange={(e) => setFormData({ ...formData, internal_number: e.target.value })}
                  placeholder="1234"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="VIP, Customer, Supplier, etc."
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional information..."
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none resize-none`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveContact}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={() => setShowModal(false)}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${theme.text} rounded-lg transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call History Modal */}
      {showCallHistory && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.bg} rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col`}>
            <div className={`p-6 border-b ${theme.border} flex items-center justify-between`}>
              <div>
                <h3 className={`text-xl font-bold ${theme.text}`}>
                  Call History - {selectedContact.full_name}
                </h3>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>
                  {selectedContact.phone_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCallHistory(false);
                  setSelectedContact(null);
                }}
                className={`${theme.textSecondary} hover:${theme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {contactCallHistory.length === 0 ? (
                <div className={`text-center py-12 ${theme.textSecondary}`}>
                  <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No call history found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contactCallHistory.map((call) => (
                    <div
                      key={call.id}
                      className={`p-4 rounded-lg border ${theme.border} ${theme.hover}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              call.direction === 'INBOUND' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {call.direction === 'INBOUND' ? '📞 Incoming' : '📱 Outgoing'}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              call.call_status === 'ANSWERED' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {call.call_status}
                            </span>
                            {call.queue_name && (
                              <span className={`text-xs ${theme.textSecondary}`}>
                                Queue: {call.queue_name}
                              </span>
                            )}
                          </div>
                          <div className={`text-sm ${theme.text}`}>
                            <span className="font-medium">{call.call_date_local}</span>
                            {call.agent_name && (
                              <span className={theme.textSecondary}> • Agent: {call.agent_name}</span>
                            )}
                          </div>
                          <div className={`text-xs ${theme.textSecondary} mt-1`}>
                            Duration: {Math.floor(call.talk_duration / 60)}:{(call.talk_duration % 60).toString().padStart(2, '0')}
                            {call.hold_duration > 0 && (
                              <span> • Hold: {Math.floor(call.hold_duration / 60)}:{(call.hold_duration % 60).toString().padStart(2, '0')}</span>
                            )}
                          </div>
                          {call.notes && (
                            <div className={`text-xs ${theme.textSecondary} mt-2 italic`}>
                              "{call.notes}"
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge Duplicates Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.bg} rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${theme.text}`}>
                Merge Duplicate Contacts ({duplicateGroups.length} groups found)
              </h2>
              <button
                onClick={() => setShowMergeModal(false)}
                className={`${theme.textSecondary} hover:${theme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {duplicateGroups.length === 0 ? (
              <div className={`text-center py-8 ${theme.textSecondary}`}>
                <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No duplicate contacts found!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {duplicateGroups.map((group, index) => (
                  <div key={index} className={`border ${theme.border} rounded-lg p-4`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className={`font-semibold ${theme.text}`}>
                          {group.cleanName}
                        </h3>
                        <p className={`text-sm ${theme.textSecondary}`}>
                          {group.contacts.length} duplicates found
                        </p>
                      </div>
                      <button
                        onClick={() => handleMergeGroup(group)}
                        disabled={loadingMerge}
                        className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                      >
                        {loadingMerge ? 'Merging...' : 'Merge'}
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.contacts.map((contact, contactIndex) => (
                        <div 
                          key={contact.id} 
                          className={`p-3 rounded border ${
                            contactIndex === 0 
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                              : 'border-red-300 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Phone className="w-4 h-4" />
                            <span className="font-mono text-sm">{contact.phone_number}</span>
                            {contactIndex === 0 && (
                              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                                KEEP
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${theme.text} mb-1`}>
                            {contact.full_name}
                          </p>
                          {contact.company && (
                            <p className={`text-xs ${theme.textSecondary}`}>
                              {contact.company}
                            </p>
                          )}
                          {contact.notes && (
                            <p className={`text-xs ${theme.textSecondary} mt-1`}>
                              Notes: {contact.notes.substring(0, 50)}...
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className={`mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm ${theme.textSecondary}`}>
                      <strong>Merge Plan:</strong> Keep "{group.contacts[0].phone_number}" as main number, 
                      {group.contacts.slice(1).some(c => c.phone_number.length <= 4) && 
                        ` set "${group.contacts.find(c => c.phone_number.length <= 4)?.phone_number}" as internal number,`
                      } delete {group.contacts.length - 1} duplicate(s)
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowMergeModal(false)}
                    className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.text} hover:${theme.hover}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced LDAP Import Modal */}
      {showLDAPImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.bg} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${theme.text}`}>
                Enhanced LDAP Import
              </h2>
              <button
                onClick={() => setShowLDAPImportModal(false)}
                className={`${theme.textSecondary} hover:${theme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Field Mapping Selection */}
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Field Mapping Configuration
                </label>
                <select
                  value={ldapImportConfig.fieldMappingId || ''}
                  onChange={(e) => setLdapImportConfig(prev => ({
                    ...prev,
                    fieldMappingId: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                >
                  <option value="">Select field mapping...</option>
                  {fieldMappings.map(mapping => (
                    <option key={mapping.id} value={mapping.id}>
                      {mapping.name} {mapping.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Defines how LDAP fields map to contact fields
                </p>
              </div>

              {/* Department Filter Selection */}
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Department Filter
                </label>
                <select
                  value={ldapImportConfig.departmentFilterId || ''}
                  onChange={(e) => setLdapImportConfig(prev => ({
                    ...prev,
                    departmentFilterId: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                >
                  <option value="">Select department filter...</option>
                  {departmentFilters.map(filter => (
                    <option key={filter.id} value={filter.id}>
                      {filter.name}
                    </option>
                  ))}
                </select>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Filter users by department or organizational unit
                </p>
              </div>

              {/* Custom Departments */}
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Custom Departments (comma-separated)
                </label>
                <input
                  type="text"
                  value={ldapImportConfig.customDepartments.join(', ')}
                  onChange={(e) => setLdapImportConfig(prev => ({
                    ...prev,
                    customDepartments: e.target.value.split(',').map(d => d.trim()).filter(Boolean)
                  }))}
                  placeholder="IT, Sales, Marketing"
                  className={`w-full px-3 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-500 outline-none`}
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Override department filter with custom list
                </p>
              </div>

              {/* Import Options */}
              <div className={`p-4 rounded-lg border ${theme.border} bg-blue-50 dark:bg-blue-900/20`}>
                <h3 className={`font-medium ${theme.text} mb-2`}>Import Behavior</h3>
                <ul className={`text-sm ${theme.textSecondary} space-y-1`}>
                  <li>• Only imports users with mobile phone numbers</li>
                  <li>• Updates existing contacts only if LDAP has more complete information</li>
                  <li>• Creates detailed import history for rollback capability</li>
                  <li>• Cleans and validates phone number formats</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleLDAPPreview}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <Search className="w-4 h-4" />
                  {loading ? 'Loading...' : 'Preview'}
                </button>
                <button
                  onClick={handleLDAPImportExecute}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  {loading ? 'Importing...' : 'Import'}
                </button>
                <button
                  onClick={() => setShowLDAPImportModal(false)}
                  className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.text} hover:${theme.hover}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import History Modal */}
      {showImportHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.bg} rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${theme.text}`}>
                LDAP Import History
              </h2>
              <button
                onClick={() => setShowImportHistory(false)}
                className={`${theme.textSecondary} hover:${theme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {importHistory.length === 0 ? (
              <div className={`text-center py-12 ${theme.textSecondary}`}>
                <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No import history found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {importHistory.map((record) => (
                  <div
                    key={record.id}
                    className={`p-4 rounded-lg border ${theme.border} ${theme.hover}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-lg font-semibold ${theme.text}`}>
                            Import #{record.id}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            record.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : record.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {record.status.toUpperCase()}
                          </span>
                          {record.rolled_back && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                              ROLLED BACK
                            </span>
                          )}
                        </div>
                        <div className={`text-sm ${theme.textSecondary} space-y-1`}>
                          <p><strong>Date:</strong> {new Date(record.import_date).toLocaleString()}</p>
                          <p><strong>Imported by:</strong> {record.imported_by}</p>
                          {record.department_filter && (
                            <p><strong>Department filter:</strong> {record.department_filter}</p>
                          )}
                          {record.rollback_date && (
                            <p><strong>Rolled back:</strong> {new Date(record.rollback_date).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!record.rolled_back && record.status === 'completed' && (
                          <button
                            onClick={() => handleRollbackImport(record.id)}
                            className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded transition-colors"
                            title="Rollback this import"
                          >
                            Rollback
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                      <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <div className={`text-lg font-bold ${theme.text}`}>{record.total_users}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>Total</div>
                      </div>
                      <div className={`p-2 rounded ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                        <div className="text-lg font-bold text-green-600">{record.imported_count}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>Imported</div>
                      </div>
                      <div className={`p-2 rounded ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                        <div className="text-lg font-bold text-blue-600">{record.updated_count}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>Updated</div>
                      </div>
                      <div className={`p-2 rounded ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-100'}`}>
                        <div className="text-lg font-bold text-yellow-600">{record.skipped_count}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>Skipped</div>
                      </div>
                      <div className={`p-2 rounded ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                        <div className="text-lg font-bold text-red-600">{record.error_count}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>Errors</div>
                      </div>
                    </div>
                    
                    {record.notes && (
                      <div className={`mt-3 p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <p className={`text-sm ${theme.textSecondary}`}>
                          <strong>Notes:</strong> {record.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactBook;
